import { BrowserAnalyticsClientFactory } from '@orthly/analytics/dist/browser';
import type {
    FragmentType,
    PatientScanCardScan_FragmentFragment,
    PatientScanCardScanExport_FragmentFragment,
    PatientScanOptionsMenuPatient_FragmentFragment,
} from '@orthly/graphql-inline-react';
import { getFragmentData, graphql, PatientScanCardScan_FragmentFragmentDoc } from '@orthly/graphql-inline-react';
import {
    FlossPalette,
    Icon,
    ListItemIcon,
    MenuItem,
    MenuList,
    Popover,
    styled,
    useScreenIsMobile,
} from '@orthly/ui-primitives';
import { useFirebaseDownload } from '@orthly/veneer';
import React from 'react';

const ScanOptionsMenuContainer = styled('div')({
    display: 'flex',
});

const StyledIcon = styled(Icon)({
    fontSize: '20px',
});

const PatientScanOptionsMenuPatient_Fragment = graphql(`
    fragment PatientScanOptionsMenuPatient_Fragment on PatientDTO {
        id
        last_name
    }
`);

interface PatientScanOptionsMenuListProps {
    patient: PatientScanOptionsMenuPatient_FragmentFragment;
    selectedScan: PatientScanCardScan_FragmentFragment | undefined | null;
    selectedScanExport: PatientScanCardScanExport_FragmentFragment | undefined | null;
    selectedScanExportLoading: boolean;
    setShareWithPatientModalOpen: (open: boolean) => void;
    handleClose: () => void;
}

const usePatientScanDownloader = (
    patient: PatientScanOptionsMenuPatient_FragmentFragment,
    selectedScan: PatientScanCardScan_FragmentFragment | undefined | null,
    selectedScanExport: PatientScanCardScanExport_FragmentFragment | undefined | null,
    selectedScanExportLoading: boolean,
) => {
    const fileExists = selectedScanExport?.file_url && !selectedScanExport?.deleted_at;
    const fileName = `${patient.last_name}_${selectedScan?.scan_type}.3oxz`;
    const lowercasedFileName = fileName.toLowerCase().replaceAll(' ', '_');
    const downloader = useFirebaseDownload(selectedScanExport?.file_url ?? undefined, lowercasedFileName);
    const isDisabled =
        selectedScanExportLoading || downloader.loading || !fileExists || selectedScanExport?.assets?.length === 0;

    return { downloader, isDisabled };
};

const DownloadScansMenuItem: React.VFC<PatientScanOptionsMenuListProps> = props => {
    const { handleClose, patient, selectedScan, selectedScanExport, selectedScanExportLoading } = props;

    const { downloader, isDisabled } = usePatientScanDownloader(
        patient,
        selectedScan,
        selectedScanExport,
        selectedScanExportLoading,
    );

    const handleClick = () => {
        BrowserAnalyticsClientFactory.Instance?.track('Practice - Patient Page - Scan Downloaded', {
            patientId: patient.id,
            scanId: selectedScan?.scan_export_id ?? '',
        });

        void downloader.execute();
        handleClose();
    };

    return (
        <MenuItem disabled={isDisabled} onClick={handleClick}>
            <ListItemIcon>
                <StyledIcon icon={'DownloadIcon'} />
            </ListItemIcon>
            Download scan files
        </MenuItem>
    );
};

const ShareScansWithPatientMenuItem: React.VFC<PatientScanOptionsMenuListProps> = props => {
    const { patient, selectedScan, selectedScanExport, selectedScanExportLoading, setShareWithPatientModalOpen } =
        props;

    const { handleClose } = props;

    const handleClick = () => {
        BrowserAnalyticsClientFactory.Instance?.track('Practice - Patient Page - Patient Share Opened', {
            patientId: patient.id,
            scanId: selectedScan?.scan_export_id ?? '',
        });

        setShareWithPatientModalOpen(true);
        handleClose();
    };

    return (
        <MenuItem
            disabled={!selectedScanExport || selectedScanExportLoading || selectedScanExport?.assets?.length === 0}
            onClick={handleClick}
        >
            <ListItemIcon>
                <StyledIcon icon={'Share'} />
            </ListItemIcon>
            Share scans with patient
        </MenuItem>
    );
};

const PatientScanOptionsMenuList: React.VFC<PatientScanOptionsMenuListProps> = props => {
    return (
        <MenuList>
            <DownloadScansMenuItem {...props} />
            <ShareScansWithPatientMenuItem {...props} />
        </MenuList>
    );
};

interface PatientScanOptionsMenuProps {
    selectedScanCardFragment: FragmentType<typeof PatientScanCardScan_FragmentFragmentDoc> | undefined | null;
    selectedScanExport: PatientScanCardScanExport_FragmentFragment | undefined | null;
    selectedScanExportLoading: boolean;
    patientFragment: FragmentType<typeof PatientScanOptionsMenuPatient_Fragment>;
    selected: boolean;
    setShareWithPatientModalOpen: (open: boolean) => void;
}

export const PatientScanOptionsMenu: React.VFC<PatientScanOptionsMenuProps> = props => {
    const {
        selectedScanCardFragment,
        selectedScanExport,
        selectedScanExportLoading,
        patientFragment,
        selected,
        setShareWithPatientModalOpen,
    } = props;

    const isMobile = useScreenIsMobile();

    const [anchorEl, setAnchorEl] = React.useState<null | SVGSVGElement>(null);

    const patient = getFragmentData(PatientScanOptionsMenuPatient_Fragment, patientFragment);
    const selectedScan = getFragmentData(PatientScanCardScan_FragmentFragmentDoc, selectedScanCardFragment);

    const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
        if (!selected) {
            return;
        }

        BrowserAnalyticsClientFactory.Instance?.track('Practice - Patient Page - Scan Actions Opened', {
            patientId: patient.id,
            scanId: selectedScan?.scan_export_id ?? '',
        });

        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    if (isMobile) {
        return null;
    }

    return (
        <ScanOptionsMenuContainer>
            <Icon icon={'MoreVertIcon'} style={{ color: FlossPalette.DARK_GRAY }} onClick={handleClick} />
            <Popover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <PatientScanOptionsMenuList
                    selectedScan={selectedScan}
                    selectedScanExport={selectedScanExport}
                    selectedScanExportLoading={selectedScanExportLoading}
                    patient={patient}
                    setShareWithPatientModalOpen={setShareWithPatientModalOpen}
                    handleClose={handleClose}
                />
            </Popover>
        </ScanOptionsMenuContainer>
    );
};
